/**
 * Simple Built-in Announcement System Configuration
 * 
 * This file contains the current "What's New" announcement that will be shown
 * to all users in the bell icon dropdown. Admin can update this through the
 * admin panel or by directly editing this file.
 * 
 * Usage:
 * - Update currentAnnouncement object to change what users see
 * - Set isActive to false to hide announcement completely
 * - Add showUntil date to auto-hide after specific date
 * - Keep items array short and concise for better UX
 */

export interface AnnouncementItem {
  id: string;
  text: string;
  icon?: string; // Optional emoji or icon
}

export interface Announcement {
  id: string;
  version: string;
  title: string;
  items: AnnouncementItem[];
  showUntil?: string; // Date string in YYYY-MM-DD format (matches existing date handling)
  isActive: boolean;
  createdAt: string; // Date string (follows existing createdAt pattern)
  updatedAt: string; // Date string (follows existing updatedAt pattern)
}

// Current announcement configuration
// Admin: Update this object to change what users see in the bell dropdown
export const currentAnnouncement: Announcement = {
  id: "v0.946",
  version: "v0.946",
  title: "What's New in v0.946",
  items: [
    {
      id: "feature-1",
      text: "Fixed Google signup/login redirect",
      icon: "🔧"
    },
    {
      id: "feature-2", 
      text: "Removed PWA welcome back notification",
      icon: "📱"
    },
    {
      id: "feature-3",
      text: "Prefilled CAPTCHA default (demo mode)",
      icon: "🛡️"
    },
    {
      id: "feature-4",
      text: "Improved Supabase configuration",
      icon: "⚡"
    }
  ],
  showUntil: "2025-08-31", // Auto-hide after this date (YYYY-MM-DD format)
  isActive: true,
  createdAt: new Date().toISOString(), // Matches existing timestamp pattern
  updatedAt: new Date().toISOString()  // Matches existing timestamp pattern
};

// Utility functions for announcement management
export const announcementUtils = {
  /**
   * Check if the current announcement should be shown
   * Uses same date comparison logic as existing pages
   */
  shouldShowAnnouncement(): boolean {
    if (!currentAnnouncement.isActive) {
      return false;
    }

    // Check if announcement has expired (using same logic as meal tracker)
    if (currentAnnouncement.showUntil) {
      const hideDate = new Date(currentAnnouncement.showUntil);
      const now = new Date();
      
      // Compare dates only (same as MealTracker logic)
      const hideDateLocal = new Date(hideDate.getFullYear(), hideDate.getMonth(), hideDate.getDate());
      const nowLocal = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      
      if (nowLocal > hideDateLocal) {
        return false;
      }
    }

    return true;
  },

  /**
   * Get the current announcement if it should be shown
   */
  getCurrentAnnouncement(): Announcement | null {
    return this.shouldShowAnnouncement() ? currentAnnouncement : null;
  },

  /**
   * Get announcement items count (for red dot indicator)
   */
  getAnnouncementCount(): number {
    const announcement = this.getCurrentAnnouncement();
    return announcement ? announcement.items.length : 0;
  },

  /**
   * Check if announcement has any content
   */
  hasAnnouncement(): boolean {
    const announcement = this.getCurrentAnnouncement();
    return announcement !== null && announcement.items.length > 0;
  },

  /**
   * Get formatted title for display
   */
  getDisplayTitle(): string {
    const announcement = this.getCurrentAnnouncement();
    return announcement ? announcement.title : "";
  },

  /**
   * Get announcement items for display
   */
  getDisplayItems(): AnnouncementItem[] {
    const announcement = this.getCurrentAnnouncement();
    return announcement ? announcement.items : [];
  },

  /**
   * Format date for display (same logic as Dashboard/MealTracker)
   */
  formatDate(dateString: string): string {
    const date = new Date(dateString);
    const now = new Date();
    
    // Create timezone-aware date comparisons using local midnight boundaries
    const dateLocal = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const todayLocal = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    // Calculate difference in days using local dates (eliminates timezone issues)
    const diffTime = todayLocal.getTime() - dateLocal.getTime();
    const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return 'Today';
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays <= 7) {
      return `${diffDays} days ago`;
    } else {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
  },

  /**
   * Get formatted update date for display
   */
  getLastUpdated(): string {
    const announcement = this.getCurrentAnnouncement();
    return announcement ? this.formatDate(announcement.updatedAt) : "";
  }
};

// Export for easy access
export default currentAnnouncement;